// b548 — EP TACTICAL OVERLAY on the Cesium photorealistic 3D globe — GLOBAL.
// Routes + evac corridors + chokepoint/overwatch/threat pins + LINE-OF-SIGHT sightlines
// (green=clear, red=blocked via scene.pickFromRay vs the 3D tileset). The scenario is
// fetched from /tactical?lat&lon CENTERED ON THE CURRENT GLOBE VIEW, so it works wherever
// the operator is (re-centers on camera move). Real per-detail scenarios replace the demo.
// b561 — INTEL TACTICAL BRIDGE: entities tagged with properties + tap -> postMessage to
// the iOS app (window.webkit.messageHandlers.atlas) so a tapped pin opens the native
// action sheet (View / Set principal / Email). Guarded: no-op off-device.
// b598 — REMOVED the b574 TACTICAL EDIT BRIDGE (place/move/delete gestures).
// It emitted targetPlace/targetMove/targetDelete to native, but native never
// grew a consumer for those three actions (grepped iOS ContentView.swift:
// 0 matches) — every long-press/drag on the globe was a silent no-op. Matches
// Brockmann's own later call (b595, ContentView.swift): "Target editing
// (place/move/set-role) belongs in BFT — native MKMapView owns its markers...
// the Cesium WKWebView cannot [reliably drag]. Cesium stays a VIEW." Killed
// rather than wired, per that decision + the no-dead-affordance rule — this
// was misleading on a life-safety tool: the "PLACE ON LONG-PRESS" chip row +
// instructions in TacticalLegendComponent.tsx removed alongside this.
// THREAT still renders as a MIL-STD-2525-style HOSTILE DIAMOND (red);
// applyOverride()/applyLiveFeed() both still merge `threats` for that render.

import { createEffect, onCleanup } from "solid-js"; // b598 — createSignal removed with placeType
import { useCesium } from "../../cesium/useCesium.ts";
import { groundState } from "./store.ts";
import { PROXY_BASE_URL } from "../../config.ts";
const RISK = {
  low: "#39d98a",
  med: "#ffcf4d",
  high: "#ff4d4d"
};
const NODEPTH = Number.POSITIVE_INFINITY;

// b598 — PLACE_TYPES/placeType/EDITABLE/gesture constants REMOVED with the
// dead edit-bridge above. Nothing else imports them (TacticalLegendComponent's
// palette removed alongside).

// b574 — MIL-STD-2525 frame: HOSTILE = diamond. Drawn once to a canvas and reused as a
// billboard image (a rotated square is the diamond frame).
let DIAMOND_URL = null;
const hostileDiamond = () => {
  if (DIAMOND_URL) return DIAMOND_URL;
  const c = document.createElement("canvas");
  c.width = 56;
  c.height = 56;
  const g = c.getContext("2d");
  g.translate(28, 28);
  g.rotate(Math.PI / 4);
  g.fillStyle = "rgba(255,77,77,0.28)";
  g.strokeStyle = "#ff4d4d";
  g.lineWidth = 5;
  g.lineJoin = "miter";
  g.fillRect(-15, -15, 30, 30);
  g.strokeRect(-15, -15, 30, 30);
  DIAMOND_URL = c.toDataURL();
  return DIAMOND_URL;
};
export function TacticalLayer() {
  const {
    viewer,
    ready
  } = useCesium();
  let ds = null;
  let moveOff = null;
  let selOff = null;
  let gestureOff = null;
  let deb = null;
  const cart = (lon, lat, alt = 0) => Cesium.Cartesian3.fromDegrees(lon, lat, alt);
  const clear = () => {
    const v = viewer();
    if (v && ds) {
      try {
        v.dataSources.remove(ds, true);
      } catch (_) {}
    }
    ds = null;
  };

  // current globe-view center as ?lat&lon so the scenario renders wherever we are
  const centerQuery = () => {
    const v = viewer();
    if (!v) return "";
    try {
      const ell = v.scene.globe.ellipsoid;
      const ctr = v.camera.pickEllipsoid(new Cesium.Cartesian2(v.canvas.clientWidth / 2, v.canvas.clientHeight / 2), ell);
      const cg = ctr ? ell.cartesianToCartographic(ctr) : v.camera.positionCartographic;
      if (!cg) return "";
      return `?lat=${Cesium.Math.toDegrees(cg.latitude).toFixed(5)}&lon=${Cesium.Math.toDegrees(cg.longitude).toFixed(5)}`;
    } catch (_) {
      return "";
    }
  };
  const losClear = (fromC, toC) => {
    const v = viewer();
    if (!v) return true;
    const dir = Cesium.Cartesian3.subtract(toC, fromC, new Cesium.Cartesian3());
    const dist = Cesium.Cartesian3.magnitude(dir);
    Cesium.Cartesian3.normalize(dir, dir);
    try {
      const hit = v.scene.pickFromRay(new Cesium.Ray(fromC, dir), []);
      if (!hit || !hit.position) return true;
      return Cesium.Cartesian3.distance(fromC, hit.position) >= dist - 8;
    } catch (_) {
      return true;
    }
  };

  // b573 — native-side tactical override (Set as Principal/Overwatch/Chokepoint
  // from the iOS action sheet). The /tactical scenario is demo data that fully
  // re-fetches + re-renders on every camera pan (see moveEnd listener below), so
  // the override has to be re-applied after EVERY fetch, not just once.
  // window.atlasSetOverride(json) is called by the native WKWebView bridge.
  const applyOverride = s => {
    const ov = window.atlasOverride;
    if (!ov) return s;
    const removed = ov.removed || [];
    const dropRemoved = list => (list || []).filter(e => !removed.includes(e.name));
    const mergeList = (base, overrides) => {
      if (!overrides || !overrides.length) return dropRemoved(base);
      const names = new Set(overrides.map(o => o.name));
      return [...dropRemoved(base).filter(b => !names.has(b.name)), ...overrides];
    };
    if (ov.principal && !removed.includes(ov.principal.name)) s.principal = ov.principal;else if (s.principal && removed.includes(s.principal.name)) s.principal = null;
    s.overwatch = mergeList(s.overwatch, ov.overwatch);
    s.chokepoints = mergeList(s.chokepoints, ov.chokepoints);
    // b574 — threats were NOT merged before, so a threat pushed by native (or removed by
    // native) had no effect on the globe at all. This is the consumer for the threat half
    // of the override.
    s.threats = mergeList(s.threats, ov.threats);
    return s;
  };

  // b598 — LIVE TACTICAL FEED (native BFT roster + real target pins, read-only,
  // Cesium stays a VIEW per b595). Separate from applyOverride/TacticalOverride
  // on purpose: TacticalOverride is operator-authored + persisted across
  // relaunch (principal/overwatch/chokepoints); this is a live BFT snapshot,
  // rebuilt on every push, never persisted. window.atlasSetLiveFeed(json) is
  // called by the native WKWebView bridge, throttled to ~3s to match
  // BlueForceTracker.broadcastIntervalSeconds.
  const applyLiveFeed = s => {
    const lf = window.atlasLiveFeed;
    if (!lf) return s;
    s.members = lf.members || [];
    // Real target pins reuse the existing hostile-diamond threat renderer +
    // LOS-to-principal lines (both already merge/iterate s.threats) — no new
    // renderer needed. Keyed by label, not name, since target pins have no
    // operator-typed name the way TacticalOverride entities do.
    const liveThreats = (lf.targets || []).map(t => ({
      name: t.label,
      lon: t.lon,
      lat: t.lat
    }));
    if (liveThreats.length) s.threats = [...(s.threats || []), ...liveThreats];
    return s;
  };

  // b598 — emit()/pickLonLat()/showReadout()/hideReadout() REMOVED. They only
  // served the dead edit-bridge gesture handler below (also removed) — nothing
  // else in this file called them.

  const build = async () => {
    const v = viewer();
    if (!v || !groundState.tacticalEnabled) return;
    let s;
    try {
      s = await (await fetch(`${PROXY_BASE_URL}/tactical${centerQuery()}`)).json();
    } catch (e) {
      console.error("[TacticalLayer] fetch failed", e);
      return;
    }
    if (!s || !groundState.tacticalEnabled) return;
    s = applyOverride(s);
    s = applyLiveFeed(s); // b598 — real BFT roster + real target pins
    clear();
    ds = new Cesium.CustomDataSource("tactical");
    v.dataSources.add(ds);

    // b561 — tag each pin with atlas* properties so a tap can identify it for the
    // iOS bridge (type/name/lat/lon read back via entity.properties.<k>.getValue()).
    // b574 — `shape` selects the MIL-STD frame: "diamond" = hostile (threat), "point" =
    // everything else (unchanged).
    const pin = (lon, lat, alt, name, color, atlasType, shape = "point") => {
      const common = {
        position: cart(lon, lat, alt || 0),
        label: {
          text: name,
          font: "bold 12px monospace",
          fillColor: Cesium.Color.fromCssColorString(color),
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 3,
          pixelOffset: new Cesium.Cartesian2(0, -18),
          disableDepthTestDistance: NODEPTH
        },
        properties: {
          atlasType,
          atlasName: name,
          atlasLat: lat,
          atlasLon: lon
        }
      };
      return ds.entities.add(shape === "diamond" ? {
        ...common,
        billboard: {
          image: hostileDiamond(),
          width: 26,
          height: 26,
          disableDepthTestDistance: NODEPTH
        }
      } : {
        ...common,
        point: {
          pixelSize: 12,
          color: Cesium.Color.fromCssColorString(color),
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 2,
          disableDepthTestDistance: NODEPTH
        }
      });
    };
    (s.routes || []).forEach(r => ds.entities.add({
      polyline: {
        positions: r.points.map(p => cart(p[0], p[1], 6)),
        width: 6,
        material: new Cesium.PolylineGlowMaterialProperty({
          color: Cesium.Color.fromCssColorString(RISK[r.risk] || RISK.low),
          glowPower: 0.25
        })
      }
    }));
    (s.corridors || []).forEach(c => ds.entities.add({
      polyline: {
        positions: c.points.map(p => cart(p[0], p[1], 4)),
        width: c.width || 30,
        material: Cesium.Color.fromCssColorString(RISK[c.risk] || RISK.med).withAlpha(0.35)
      }
    }));
    if (s.principal) pin(s.principal.lon, s.principal.lat, 0, s.principal.name || "PRINCIPAL", "#ffd24d", "principal");
    (s.chokepoints || []).forEach(c => pin(c.lon, c.lat, 0, c.name, RISK[c.risk] || RISK.med, "chokepoint"));
    (s.overwatch || []).forEach(o => pin(o.lon, o.lat, o.alt || 0, o.name, "#33e0ff", "overwatch"));
    // b574 — hostile diamond frame (MIL-STD-2525: hostile = diamond).
    (s.threats || []).forEach(t => pin(t.lon, t.lat, t.alt || 0, t.name, "#ff4d4d", "threat", "diamond"));
    (s.drones || []).forEach(d => pin(d.lon, d.lat, d.alt || 120, "⊗ " + (d.name || "DRONE"), "#ff2db5", "drone"));
    // b598 — live BFT roster. Distinct small dot from PRINCIPAL/OW/CP/THREAT so
    // it doesn't compete visually. Role "Principal" skipped here — that member
    // is already drawn via s.principal above; this avoids a duplicate pin.
    (s.members || []).forEach(m => {
      if (m.role === "Principal") return;
      const color = m.online ? "#7fd8ff" : "#5b6b80";
      pin(m.lon, m.lat, 0, m.name, color, "member");
    });
    if (s.principal) {
      const pC = cart(s.principal.lon, s.principal.lat, 2);
      const drawLos = o => {
        const oC = cart(o.lon, o.lat, o.alt || 0);
        const ok = losClear(oC, pC);
        ds.entities.add({
          polyline: {
            positions: [oC, pC],
            width: 2,
            material: (ok ? Cesium.Color.LIME : Cesium.Color.RED).withAlpha(0.8)
          }
        });
      };
      (s.overwatch || []).forEach(drawLos);
      (s.threats || []).forEach(drawLos);
    }
    v.scene.requestRender();
    console.log("[TacticalLayer] rendered at", centerQuery());
  };
  const rebuildDebounced = () => {
    if (deb) clearTimeout(deb);
    deb = setTimeout(() => {
      if (groundState.tacticalEnabled) build();
    }, 1200);
  };
  createEffect(() => {
    if (!ready()) return;
    const v = viewer();
    if (groundState.tacticalEnabled) {
      build();
      // follow the operator: re-center the scenario when the globe view settles elsewhere
      // 2026-07-13 Brockmann: markers must NOT move on pan (geo-pinned). Disabled the
      // moveEnd re-center that made the demo scenario chase the camera. Build once + stay put.
      void rebuildDebounced;
      void moveOff; // keep symbols referenced (no re-center)
      // b561 — INTEL bridge: native→web focus callback + tap→postMessage to iOS.
      if (v) {
        // 2026-08-31 SEARCH FIX (Brockmann: "search cities does not work").
        // Search was NOT broken — the form submits, /geocode resolves (verified live),
        // flyToPOI runs. The camera was being YANKED BACK. Native pushFocus() polls
        // for this function up to 40x at 250ms — a 10 SECOND window
        // (ContentView.swift ~6619) — and centerOnOperator re-asserts across a boot
        // timer. A search inside that window flies to the target and is then
        // overridden by the GPS re-centre, so the operator watches it snap home and
        // reasonably concludes search is broken.
        //
        // RULE: an explicit operator action beats automatic re-centering. After a
        // search this becomes a no-op for the rest of the session. Auto-centre still
        // works normally on a cold load where the operator has not searched.
        window.atlasFocusEntity = (lat, lon) => {
          if (window.__atlasOperatorMovedCamera) {
            console.log("[TacticalLayer] atlasFocusEntity ignored — operator searched; their intent wins");
            return;
          }
          v.camera.flyTo({
            destination: Cesium.Cartesian3.fromDegrees(lon, lat, 1500)
          });
        };
        // b573 — native pushes the persisted override here; re-render immediately
        // so Set as Principal/Overwatch/Chokepoint shows up without waiting for
        // the next camera pan.
        window.atlasSetOverride = jsonStr => {
          try {
            window.atlasOverride = JSON.parse(jsonStr);
          } catch (e) {
            console.error("[TacticalLayer] bad override JSON", e);
            return;
          }
          build();
        };

        // b598 — LIVE TACTICAL FEED consumer. Native pushes the real BFT roster
        // + real target pins here (throttled ~3s, matches BFT's own broadcast
        // cadence); re-render immediately so the globe reflects the team
        // without waiting for a camera pan. Read-only — no gesture, no bridge
        // message emitted back for members/targets (Cesium stays a VIEW, b595).
        window.atlasSetLiveFeed = jsonStr => {
          try {
            window.atlasLiveFeed = JSON.parse(jsonStr);
          } catch (e) {
            console.error("[TacticalLayer] bad live-feed JSON", e);
            return;
          }
          build();
        };

        // b574 — suppress the tap→tacticalSelect that Cesium would otherwise fire on the
        // LEFT_UP of a long-press-delete or a drag-release (which would pop the native
        // action sheet on top of the delete/move the operator just performed).
        let suppressSelect = false;
        if (!selOff) {
          const onSel = e => {
            if (suppressSelect) {
              suppressSelect = false;
              return;
            } // b574
            const p = e?.properties;
            if (!p) return;
            const get = k => p[k]?.getValue?.() ?? p[k];
            const type = get("atlasType");
            if (!type) return; // only OUR tactical pins
            const bridge = window.webkit?.messageHandlers?.atlas;
            if (!bridge) return; // no-op off-device
            bridge.postMessage({
              action: "tacticalSelect",
              type,
              name: get("atlasName") ?? "",
              lat: get("atlasLat") ?? null,
              lon: get("atlasLon") ?? null
            });
          };
          v.selectedEntityChanged.addEventListener(onSel);
          selOff = () => {
            if (!v.isDestroyed()) v.selectedEntityChanged.removeEventListener(onSel);
          };
        }

        // b598 — b574 PLACE/MOVE/DELETE gesture handler REMOVED (dead wire: no
        // native consumer ever existed for targetPlace/targetMove/targetDelete —
        // grepped ContentView.swift, 0 matches). gestureOff stays declared above
        // for the harmless no-op cleanup calls below/in onCleanup.
      }
    } else {
      clear();
      if (moveOff) {
        moveOff();
        moveOff = null;
      }
      if (selOff) {
        selOff();
        selOff = null;
      }
      if (gestureOff) {
        gestureOff();
        gestureOff = null;
      }
    }
  });
  onCleanup(() => {
    clear();
    if (moveOff) moveOff();
    if (selOff) selOff();
    if (gestureOff) gestureOff();
    if (deb) clearTimeout(deb);
    // b598 — hideReadout() call removed with the function itself (dead-gesture cleanup)
  });
  return null;
}
export default TacticalLayer;